// src/services/conversationService.ts

import { CNS_API_BASE } from "@/config";
import { CNSConversation, CNSCreateConversation, CNSConversationPage, CNSChatMemorySummary, CNSChatMemorySummaryPage, CNSMessage, CNSMessagePage, type CNSActionSubmissionResponse } from "@/models/cns";
import {
  parseActionContinuationReservationResponseWire,
  parseActionSubmissionResponseWire,
  type ActionContinuationReservationResponse,
  type ActionContinuationReservationRequest,
} from "@/types/actionRequest.schema";
import type { ChatMessagePart } from "@/lib/mentions/types";
import { throwOnProblemResponse } from "@/types/problem-detail";
import {
  conversationStreamEnvelopeSchema,
  type ConversationStreamEnvelope,
} from "@/types/conversationStream.schema";
import type { ConversationStreamChunk } from "@/types/conversationStream";

const CONVERSATIONS_URL = `${CNS_API_BASE}/conversations`;

/** Get a single conversation by ID, including the backend-authoritative actor access projection. */
export async function fetchConversationById(conversationId: string): Promise<CNSConversation> {
  const res = await fetch(`${CNS_API_BASE}/conversations/${encodeURIComponent(conversationId)}`, {
    credentials: "include",
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to load conversation");
  return res.json();
}

/** Recent conversations for the current user across all projects (paginated). Optionally filter by workspace. Each item includes projectId (project public_id). */
export async function fetchRecentConversationsForCurrentUser(
  page = 0,
  size = 10,
  workspaceId?: string | null
): Promise<CNSConversationPage> {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  if (workspaceId != null && workspaceId !== "") {
    params.set("workspaceId", workspaceId);
  }
  const res = await fetch(`${CNS_API_BASE}/conversations/users/current/recent?${params}`, {
    credentials: "include",
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch recent conversations");
  return res.json();
}

// Fetch all conversations
export async function fetchConversations(): Promise<CNSConversation[]> {
  const res = await fetch(CONVERSATIONS_URL, { credentials: "include" });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch conversations");
  const data: CNSConversationPage = await res.json();
  return data.content || [];
}

// Create a new conversation
export async function createConversation(title: string, projectId: string): Promise<CNSConversation> {
  const res = await fetch(CONVERSATIONS_URL, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title, projectId }),
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to create conversation");
  return res.json();
}

// Delete a conversation
export async function deleteConversation(conversationId: string): Promise<void> {
  const res = await fetch(`${CONVERSATIONS_URL}/${conversationId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to delete conversation");
}

// Fetch chat memory for a conversation
export async function fetchChatMemory(conversationId: string): Promise<CNSChatMemorySummary[]> {
  const url = `${CONVERSATIONS_URL}/${conversationId}/chat-memory`;
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch chat memory");
  const data: CNSChatMemorySummaryPage = await res.json();
  return data.content || [];
}

// Fetch messages for a conversation
export async function fetchMessages(
  conversationId: string,
  page?: number,
  size?: number
): Promise<CNSMessage[]> {
  const url = `${CONVERSATIONS_URL}/${conversationId}/messages`;
  const params = new URLSearchParams();
  if (page !== undefined) params.append('page', page.toString());
  if (size !== undefined) params.append('size', size.toString());
  
  const res = await fetch(`${url}?${params}`, { credentials: "include" });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch messages");
  const data: CNSMessagePage = await res.json();
  return data.content || [];
}

// Stream chat response for a conversation (SSE)
/**
 * POST conversations/{id}/chat with userInput and optional messageParts (ordered text/mention composition).
 * {@code messageParts} is the FE→BE contract of record for turn @-mentions; the backend derives validated
 * mentions from mention parts only (not from a separate mentions array). When omitted or empty, the turn
 * is plain userInput. Non-OAuth Action Request continuations send only toolCallId plus the
 * server-reserved assistantMessageId; the backend rejects browser-authored text, memory ids,
 * attachments, and message parts for that assistant-only turn. sourceOAuthAttemptId retains its
 * specialized callback claim protocol and likewise has no new user-memory identity.
 * Returns the raw Response for SSE parsing; does not throw on non-OK status before the caller reads the body.
 */
export async function streamConversationChat(
  conversationId: string,
  userInput: string,
  abortSignal?: AbortSignal,
  toolCallId?: string,
  _toolData?: string, // Deprecated: Action Request input is submitted separately; do not send in /chat body
  chatMemoryId?: string,
  documentIds?: string[], // Document public IDs (KSUIDs)
  assistantMessageId?: string, // Persisted placeholder id: browser-created for ordinary turns, server-reserved for Action Requests
  messageParts?: ChatMessagePart[], // Ordered message composition; backend derives validated mentions from mention parts
  sourceOAuthAttemptId?: string, // Claims a callback-persisted connect_source continuation
): Promise<Response> {
  const url = `${CONVERSATIONS_URL}/${conversationId}/chat`;

  const requestBody = {
    userInput,
    ...(toolCallId && { toolCallId }),
    ...(chatMemoryId && { chatMemoryId }),
    ...(documentIds && documentIds.length > 0 && { documentIds }),
    ...(assistantMessageId && { assistantMessageId }),
    ...(messageParts && messageParts.length > 0 && { messageParts }),
    ...(sourceOAuthAttemptId && { sourceOAuthAttemptId }),
  };

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Accept': 'text/event-stream',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    signal: abortSignal,
    body: JSON.stringify(requestBody),
  });
  if (!response.ok) {
    await throwOnProblemResponse(response, `API request failed with status: ${response.status}`);
  }
  return response;
}

/** Submit strict action-specific input to the addressed durable Action Request. */
export async function submitActionRequest(
  conversationId: string,
  actionRequestId: string,
  submission: Record<string, unknown>,
): Promise<CNSActionSubmissionResponse> {
  const url = `${CONVERSATIONS_URL}/${encodeURIComponent(conversationId)}/action-requests/${encodeURIComponent(actionRequestId)}/submission`;
  const res = await fetch(url, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ submission }),
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to submit action");
  return parseActionSubmissionResponseWire(await res.json());
}

/**
 * Idempotently reserves the server-owned assistant message for one completed Action Request.
 * The backend generates or reuses the canonical message identity; the browser supplies only the
 * selected transcript parent.
 */
export async function reserveActionRequestContinuation(
  conversationId: string,
  actionRequestId: string,
  request: ActionContinuationReservationRequest,
): Promise<ActionContinuationReservationResponse> {
  const url = `${CONVERSATIONS_URL}/${encodeURIComponent(conversationId)}/action-requests/${encodeURIComponent(actionRequestId)}/continuation`;
  const res = await fetch(url, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    await throwOnProblemResponse(
      res,
      "Failed to reserve the assistant continuation",
    );
  }
  return parseActionContinuationReservationResponseWire(await res.json());
}

/** Parse converse SSE into P1 {@link ConversationStreamEnvelope} or structured error chunks. */
export class ConversationStreamProtocolError extends Error {
  readonly code = "conversation-stream-protocol-error";

  constructor(message: string) {
    super(message);
    this.name = "ConversationStreamProtocolError";
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function legacyStreamErrorChunk(jsonData: unknown): ConversationStreamChunk {
  if (!isRecord(jsonData)) {
    throw new ConversationStreamProtocolError(
      "Conversation stream contained an invalid error payload.",
    );
  }
  const payload = isRecord(jsonData.payload) ? jsonData.payload : jsonData;
  const detail = (payload.detail ?? jsonData.errorDetail ?? jsonData.detail) as
    | string
    | undefined;
  const title = (payload.title ?? jsonData.errorTitle ?? jsonData.title) as
    | string
    | undefined;
  const code = (payload.code ?? jsonData.code ?? jsonData.errorCode) as
    | string
    | undefined;
  const status =
    typeof payload.status === "number"
      ? payload.status
      : typeof jsonData.status === "number"
        ? jsonData.status
        : undefined;
  return { event: "error", error: { detail, title, code, status } };
}

function parseConversationSseData(
  dataString: string,
  currentEvent: string | null,
): ConversationStreamChunk {
  let jsonData: unknown;
  try {
    jsonData = JSON.parse(dataString) as unknown;
  } catch {
    throw new ConversationStreamProtocolError(
      "Conversation stream contained malformed JSON.",
    );
  }

  if (currentEvent === "error") {
    const typedErrorEnvelope = conversationStreamEnvelopeSchema.safeParse(jsonData);
    if (typedErrorEnvelope.success) {
      return typedErrorEnvelope.data;
    }
    return legacyStreamErrorChunk(jsonData);
  }

  const envelopeResult = conversationStreamEnvelopeSchema.safeParse(jsonData);
  if (!envelopeResult.success) {
    const eventType =
      isRecord(jsonData) && "eventType" in jsonData
        ? String(jsonData.eventType)
        : "unknown";
    throw new ConversationStreamProtocolError(
      `Conversation stream contained an invalid ${eventType} envelope.`,
    );
  }

  const envelope: ConversationStreamEnvelope = envelopeResult.data;
  return envelope;
}

export async function* parseSSEStream(
  stream: ReadableStream<Uint8Array>,
  abortSignal?: AbortSignal
): AsyncGenerator<ConversationStreamChunk, void, unknown> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent: string | null = null;
  try {
    while (true) {
      // Check if aborted before reading
      if (abortSignal?.aborted) {
        throw new DOMException('Stream aborted', 'AbortError');
      }

      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        // Check if aborted during processing
        if (abortSignal?.aborted) {
          throw new DOMException('Stream aborted', 'AbortError');
        }

        if (line.startsWith("event:")) {
          currentEvent = line.substring(6).trim();
          continue;
        }

        if (line.startsWith("data:")) {
          const dataString = line.substring(5).trim();
          if (dataString) {
            yield parseConversationSseData(dataString, currentEvent);
          }
          currentEvent = null;
          continue;
        }
      }
    }
    // Handle any remaining data in the buffer (only if not aborted)
    if (!abortSignal?.aborted && buffer.startsWith("data:")) {
      const dataString = buffer.substring(5).trim();
      if (dataString) {
        yield parseConversationSseData(dataString, currentEvent);
      }
    }
  } catch (error) {
    // Re-throw abort errors
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    // Re-throw other errors
    throw error;
  } finally {
    reader.releaseLock();
  }
}
